import { SliceZone, KeyTextField } from "@prismicio/types";
import _ from "lodash";
import _isPlainObject from "lodash/isPlainObject";
import { useRouter } from "next/router";

import { GiftwrapExtraSlice, AddOnExtraSlice } from "prismicio-types";
import { useGetProduct } from "queries/legacy/product-queries";
import { EagleItem, UseGetProductResponse } from "types/queries";

import { SSR_CONFIGURATION_ERROR } from "./error-utils";

export function useGetValidComponentsForFormat() {
  const router = useRouter();

  const { data: product } = useGetProduct();

  /**
   * Get valid customisation `components` array for this format.
   *
   * Used to ensure format edits don't create invalid giftwrap/add-ons combinations.
   *
   * Returns a new `components` array.
   */
  function getValidComponentsForFormat({
    components,
    newFormatIdentifier,
  }: {
    components: { id: string }[];
    newFormatIdentifier: string;
  }) {
    if (!product) {
      throw SSR_CONFIGURATION_ERROR;
    }

    const giftwrapIndex = components.findIndex((c) =>
      (c.id as string).includes("giftwrap")
    );

    // If we have a giftwrap
    if (giftwrapIndex > -1) {
      const giftwrapSku = components[giftwrapIndex].id;

      // The giftwrap is valid if...
      const giftwrapIsValid = product!.content.extras.giftwrapSlices.some(
        (slice) =>
          // The SKU matches a slice
          slice.primary.sku === giftwrapSku &&
          // Which applies to this format
          slice.primary.active_formats
            ?.split(",")
            .find((format) => format === newFormatIdentifier) &&
          // And isn't hidden for this product
          !slice.primary.inactive_solidus_slugs
            ?.split(",")
            .find((slug) => slug === router.query.solidus_slug) &&
          // And isn't hidden for this marketplace
          !slice.primary.inactive_markets
            ?.split(",")
            .find((market) => market === router.query.marketplace)
      );

      if (!giftwrapIsValid) {
        // If it's no longer valid, remove from the components array.
        components.splice(giftwrapIndex, 1);
      }
    }

    return components;
  }

  return getValidComponentsForFormat;
}

/**
 * Add or replace an existing giftwrap in the `components` array.
 *
 * Returns a new `components` array.
 */
export function getComponentsForGiftwrap({
  components,
  giftwrapSku,
}: {
  components: { id: string }[];
  giftwrapSku?: string;
}) {
  // Do we already have a giftwrap?
  const giftwrapIndex = components.findIndex((c) =>
    (c.id as string).includes("giftwrap")
  );

  const hasExistingGiftwrap = giftwrapIndex > -1;
  const newComponents = [...components];

  // The user has submitted a giftwrap to add
  if (giftwrapSku) {
    // If we already have one added
    if (hasExistingGiftwrap) {
      // Update the previously added giftwrap
      newComponents[giftwrapIndex] = { id: giftwrapSku };
    } else {
      // Otherwise push as a new component
      newComponents.push({ id: giftwrapSku });
    }
  } else if (hasExistingGiftwrap) {
    // If form field is `undefined` (i.e. no selection), remove previously added giftwrap
    newComponents.splice(giftwrapIndex, 1);
  }

  return newComponents;
}

/**
 * The subset of Giftwraps that should be shown to the user.
 *
 * Returns a new `giftwrap slices` array.
 */
export function getActiveGiftwrapSlices(
  giftwrapSlices: SliceZone<GiftwrapExtraSlice>,
  formatIdentifier: string,
  solidusSlug: string,
  marketplace: string
) {
  const activeGiftwrapSlices = giftwrapSlices.filter(
    (slice) =>
      // Filter out the giftwraps that don't apply to this format
      slice.primary.active_formats
        ?.split(",")
        .find((format) => format === formatIdentifier) &&
      // Or are hidden for this product
      !slice.primary.inactive_solidus_slugs
        ?.split(",")
        .find((slug) => slug === solidusSlug) &&
      // Or are hidden on this marketplace
      !slice.primary.inactive_markets
        ?.split(",")
        .find((market) => market === marketplace)
  );

  return activeGiftwrapSlices;
}

/**
 * The subset of Addons that should be shown to the user.
 *
 * Returns a new `addon slices` array.
 */
export function getActiveAddonSlices(
  addOnSlices: SliceZone<AddOnExtraSlice>,
  solidusSlug: string,
  marketplace: string,
  productUpsells: EagleItem[]
) {
  const activeAddOnSlices = addOnSlices.filter((slice) => {
    if (
      slice.primary.active_solidus_slugs &&
      !slice.primary.active_solidus_slugs
        .split(",")
        .find((slug) => slug === solidusSlug)
    ) {
      // Filter if we have a product whitelist and this product isn't in it
      return false;
    } else if (
      slice.primary.inactive_solidus_slugs &&
      slice.primary.inactive_solidus_slugs
        .split(",")
        .find((slug) => slug === solidusSlug)
    ) {
      // Or we have a product blacklist and this product is in it
      return false;
    }

    if (
      slice.primary.inactive_markets &&
      slice.primary.inactive_markets
        .split(",")
        .find((market) => market === marketplace)
    ) {
      // Or we have a market blacklist and this market is in it
      return false;
    }

    const addOn = getSolidusAddon(productUpsells, slice.primary.sku);
    if (!addOn) {
      // Don't show add-ons that aren't in Solidus
      return false;
    }

    // Otherwise we're good to show it
    return true;
  });

  return activeAddOnSlices;
}

/**
 * The subset of Addons that are availble in Solidus.
 *
 * Returns an active `addon` or null.
 */
export function getSolidusAddon(
  productUpsells: EagleItem[],
  slicePrimarySku: KeyTextField
) {
  let addOn: EagleItem["variants"][number] | null = null;
  for (let i = 0; i < productUpsells.length; i++) {
    // Check all `pre-printed` entries in `upsells`
    if (productUpsells[i].category === "pre-printed") {
      // For a `sku` that matches this slice
      const variant = productUpsells[i].variants?.find(
        ({ sku }: { sku: string }) => sku === slicePrimarySku
      );
      if (variant) {
        addOn = variant;
        break;
      }
    }
  }
  return addOn;
}

/**
 * The Returns the id category or selection as string.
 *
 * For complex value will return `true` or `false`.
 * e.g. dedication_photo_upload:true
 */

const getUpsellid = (customisationName: string, customisationValue: string) => {
  let variantValue;

  // Not a string, likely an object
  // we will return an id "dedication_photo_upload:true"
  if (!customisationValue.length) {
    variantValue = "true";
  }

  // If the string is empty, the selected radio option is false
  // we will return an id "dedication_photo_upload:false"
  if (customisationValue.length === 0) {
    variantValue = "false";
  }

  return `${customisationName}:${variantValue || customisationValue}`.toLocaleLowerCase();
};

/**
 * The subset of Addons that are availble in Solidus.
 *
 * Returns an active `addon` or null.
 */

export function getUpsellPriceFromProduct(
  customisationName: string,
  customisationValue: string,
  product: UseGetProductResponse
) {
  if (!customisationName === null || !customisationValue === null) {
    return null;
  }

  const variantId = getUpsellid(
    customisationName,
    customisationValue
  ) as string;

  if (!product?.upsells) {
    return null;
  }

  const upsell = product?.upsells.find(
    (upsell) => upsell.slug === customisationName
  );

  if (upsell) {
    const { variants } = upsell;
    const variant = variants.find((v) => v.sku === variantId);

    if (variant) {
      return Number(variant.price.amount);
    }
  }

  return null;
}
